'use client';

import { useEffect, useRef } from 'react';
import { isAppEnvironment } from '@/lib/fcm';

/**
 * Native-app bridge for the Capacitor shell.
 * Runs only inside the app (not in the plain web browser):
 *  - tints the Android status bar to match the dark theme,
 *  - hides the native splash screen right after first paint so the
 *    live site feels responsive as soon as it is usable (hard fallback),
 *  - Android hardware back button navigates the web history instead of
 *    killing the app (native feel),
 *  - subtle haptic feedback on taps of interactive elements,
 *  - marks the shell with `native-app` so CSS can apply app-only polish.
 */
export default function AppBridge() {
  const lastHaptic = useRef(0);
  useEffect(() => {
    if (!isAppEnvironment()) return;
    // Capacitor injects plugins only in the native shell; access loosely
    // to keep this file type-safe without depending on @capacitor packages.
    const plugins = (window as any).Capacitor?.Plugins;
    const cleanups: Array<() => void> = [];

    const statusBar = plugins?.StatusBar;
    if (statusBar) {
      statusBar.setStyle?.({ style: 'LIGHT' }).catch?.(() => {});
      statusBar.setOverlaysWebView?.({ overlay: true }).catch?.(() => {});
      statusBar.setBackgroundColor?.({ color: '#0B0E14' }).catch?.(() => {});
    }

    // App-only polish hooks (CSS targets .native-app).
    document.documentElement.classList.add('native-app');
    cleanups.push(() => document.documentElement.classList.remove('native-app'));

    // Android hardware back: prefer in-app history navigation; only exit at root.
    const app = plugins?.App;
    if (app) {
      const sub = app.addListener?.('backButton', () => {
        try {
          if (window.history.length > 1) {
            window.history.back();
          } else {
            app.exitApp?.();
          }
        } catch {
          app.exitApp?.();
        }
      });
      if (sub?.then) {
        sub.then((h: any) => cleanups.push(() => h?.remove?.())).catch?.(() => {});
      }
    }

    // v0.8: 深链全路由打通 —— 外部链接 / 通知直达（ailumix:// 或 https://www.ailumix.com/...）
    // 归一化到站内路径后跳转，保证「点通知直接进对应页」。
    const normalizeDeepLink = (raw: string): string | null => {
      try {
        const u = new URL(raw);
        const host = u.hostname.toLowerCase();
        if (u.protocol === 'ailumix:' || host === 'www.ailumix.com' || host === 'ailumix.com') {
          const p = u.pathname;
          if (p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/portal-') && p !== '/admin') {
            return p + u.search;
          }
        }
        return null;
      } catch {
        return null;
      }
    };
    if (app) {
      const sub2 = app.addListener?.('appUrlOpen', (e: { url?: string }) => {
        if (!e?.url) return;
        const path = normalizeDeepLink(e.url);
        if (path) window.location.assign(path);
      });
      if (sub2?.then) {
        sub2.then((h: any) => cleanups.push(() => h?.remove?.())).catch?.(() => {});
      }
    }

    // Light haptic tick on taps of interactive elements (throttled).
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const hit = t.closest?.(
        'button, a, [role="button"], .cursor-pointer, input, select, textarea, [onclick], label',
      );
      if (!hit) return;
      const now = Date.now();
      if (now - lastHaptic.current < 80) return;
      lastHaptic.current = now;
      plugins?.Haptics?.impact?.({ style: 'LIGHT' }).catch?.(() => {});
    };
    document.addEventListener('click', onClick, true);
    cleanups.push(() => document.removeEventListener('click', onClick, true));

    const hideSplash = () => plugins?.SplashScreen?.hide?.().catch?.(() => {});
    // Give the first paint a moment, then dismiss the splash.
    const raf = requestAnimationFrame(() => setTimeout(hideSplash, 400));
    // Hard fallback: never keep the splash forever if the page stalls.
    const fallback = setTimeout(hideSplash, 9000);
    cleanups.push(() => {
      cancelAnimationFrame(raf);
      clearTimeout(fallback);
    });

    return () => cleanups.forEach((fn) => fn());
  }, []);
  return null;
}
